import { useState } from 'react';

const YOUTUBE_URL = 'https://youtu.be/Vy-a_kdkyoY';

function getYouTubeId(url: string): string | null {
  if (!url) return null;
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
    /^([a-zA-Z0-9_-]{11})$/,
  ];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  return null;
}

export default function VideoSection() {
  const [isPlaying, setIsPlaying] = useState(false);
  const videoId = getYouTubeId(YOUTUBE_URL);

  return (
    <section id="video" className="py-20 md:py-32" style={{ background: '#0A0A0A' }}>
      <div className="max-w-4xl mx-auto px-4 md:px-6">
        <div className="text-center mb-10 md:mb-14">
          <div className="inline-flex items-center gap-2 bg-white/8 text-[#AFA9EC] text-xs font-semibold px-4 py-2 rounded-full mb-5 tracking-wide uppercase border border-white/10">
            <i className="ri-play-circle-line"></i>
            Video informativo
          </div>
          <h2 className="font-title text-3xl md:text-5xl text-white leading-tight mb-4">
            Conoce como funciona en{' '}
            <span style={{ color: '#AFA9EC' }}>menos de 2 minutos</span>
          </h2>
        </div>

        <div className="mb-8 md:mb-10">
          <div
            className="relative w-full rounded-2xl overflow-hidden"
            style={{
              paddingBottom: '56.25%',
              background: 'linear-gradient(135deg, #1a1030 0%, #2d2560 100%)',
              border: '1px solid rgba(175,169,236,0.2)',
            }}
          >
            {videoId && isPlaying ? (
              <iframe
                className="absolute inset-0 w-full h-full"
                src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1`}
                title="Video explicativo de Coverly"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <div className="absolute inset-0">
                {videoId ? (
                  <img
                    src={`https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`}
                    alt="Miniatura del video"
                    className="absolute inset-0 w-full h-full object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
                    }}
                  />
                ) : (
                  <div
                    className="absolute inset-0 bg-cover bg-center opacity-25"
                    style={{
                      backgroundImage: `url('https://readdy.ai/api/search-image?query=warm%20family%20moment%20at%20home%2C%20soft%20natural%20light%2C%20people%20smiling%20and%20embracing%2C%20cozy%20living%20room%20setting%2C%20golden%20hour%20light%2C%20lifestyle%20photography%2C%20no%20hospital%20no%20funeral%2C%20peaceful%20and%20hopeful%20atmosphere%2C%20muted%20warm%20tones%2C%20cinematic%20quality&width=1280&height=720&seq=cfvideo2025&orientation=landscape')`,
                    }}
                  />
                )}

                <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(10,5,25,0.2) 0%, rgba(10,5,25,0.55) 100%)' }} />

                <div className="absolute inset-0 flex flex-col items-center justify-center gap-4">
                  <button
                    type="button"
                    onClick={() => videoId ? setIsPlaying(true) : undefined}
                    className={`w-16 h-16 md:w-20 md:h-20 flex items-center justify-center rounded-full transition-all duration-300 ${videoId ? 'cursor-pointer hover:scale-110' : 'cursor-default opacity-60'}`}
                    style={{ background: 'rgba(83,74,183,0.9)', border: '2px solid rgba(175,169,236,0.5)' }}
                    aria-label="Reproducir video"
                  >
                    <i className="ri-play-fill text-white text-2xl md:text-3xl ml-1"></i>
                  </button>
                  <div className="text-center px-4">
                    <p className="text-white font-semibold text-sm md:text-base">Video explicativo de Coverly</p>
                    <p className="text-white/50 text-xs mt-1">
                      {videoId ? 'Haz clic para reproducir' : 'Proximamente disponible'}
                    </p>
                  </div>
                </div>

                {!videoId && (
                  <div className="absolute bottom-4 right-4 bg-black/60 text-white text-xs px-3 py-1 rounded-full backdrop-blur-sm">
                    <i className="ri-time-line mr-1"></i>
                    2:00 min
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <p className="text-white/55 text-sm md:text-base leading-relaxed text-center max-w-2xl mx-auto font-light mb-12 md:mb-16">
          Te explicamos paso a paso como activar tu membresia y solicitar la coordinacion de servicios conforme al plan.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6">
          {[
            { step: '01', icon: 'ri-user-add-line', title: 'Activa tu membresia', desc: 'Elige tu plan y recibe tu folio por WhatsApp en minutos.' },
            { step: '02', icon: 'ri-group-line', title: 'Registra familiares', desc: 'Agrega a los miembros de tu familia que quieres registrar.' },
            { step: '03', icon: 'ri-shield-check-line', title: 'Solicita coordinacion', desc: 'Cuando corresponda, te orientamos para iniciar el proceso.' },
          ].map((item) => (
            <div
              key={item.step}
              className="flex flex-col items-center text-center p-5 md:p-6 rounded-2xl"
              style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(175,169,236,0.12)' }}
            >
              <div className="text-[#AFA9EC]/40 text-xs font-bold tracking-widest mb-3">{item.step}</div>
              <div className="w-10 h-10 flex items-center justify-center rounded-xl mb-3" style={{ background: 'rgba(83,74,183,0.25)' }}>
                <i className={`${item.icon} text-[#AFA9EC] text-lg`}></i>
              </div>
              <h4 className="text-white font-semibold text-sm mb-2">{item.title}</h4>
              <p className="text-white/45 text-xs leading-relaxed font-light">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
